import { useState, useCallback, useEffect } from "react";

// 表格选择对话框配置接口（兼容旧配置，但推荐使用 GenericModalTable 的配置）
export interface TableSelectModalConfig {
  title: string; // 对话框标题
  width?: number | string; // 对话框宽度
  height?: number | string; // 表格高度（已废弃，使用 fixedHeight）
  fixedHeight?: number | string; // 固定高度模式下的Modal body高度
  confirmText?: string; // 确认按钮文本
  cancelText?: string; // 取消按钮文本
  loading?: boolean; // 加载状态
  selectionType?: "checkbox" | "radio"; // 选择类型
  multiple?: boolean; // 是否多选（已废弃，使用 selectionType）
  showConfirm?: boolean; // 是否显示确认按钮
  showCancel?: boolean; // 是否显示取消按钮
}

// Hook 配置接口
export interface UseTableSelectModalConfig<T = any> {
  dataSource: T[]; // 数据源
  loading?: boolean; // 加载状态
  onDataChange?: () => void; // 数据变化回调
  onSelectionChange?: (selectedKeys: React.Key[], selectedRows: T[]) => void; // 选择变化回调
}

// Hook 返回接口
export interface UseTableSelectModalReturn<T = any> {
  // 状态
  visible: boolean;
  selectedKeys: React.Key[];
  selectedRows: T[];
  loading: boolean;

  // 配置
  config: TableSelectModalConfig;

  // 方法
  openModal: (initialConfig?: Partial<TableSelectModalConfig>) => void;
  closeModal: () => void;
  setConfig: (config: Partial<TableSelectModalConfig>) => void;
  setLoading: (loading: boolean) => void;
  setSelectedKeys: (keys: React.Key[]) => void;
  setSelectedRows: (rows: T[]) => void;
  handleSelectionChange: (selectedKeys: React.Key[], selectedRows: T[]) => void;
  handleConfirm: (onConfirm?: (selectedKeys: React.Key[], selectedRows: T[]) => void) => void;
  handleCancel: () => void;
  handleClose: () => void;
}

/**
 * 表格选择对话框 Hook
 * 提供表格选择对话框的状态管理和操作方法
 */
export const useTableSelectModal = <T extends Record<string, any>>({
  dataSource,
  loading = false,
  onDataChange,
  onSelectionChange,
}: UseTableSelectModalConfig<T>): UseTableSelectModalReturn<T> => {
  // 状态管理
  const [visible, setVisible] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [internalLoading, setInternalLoading] = useState(loading);
  const [config, setConfigState] = useState<TableSelectModalConfig>({
    title: "选择数据",
    width: 800,
    height: "400px",
    fixedHeight: 500,
    confirmText: "确认",
    cancelText: "取消",
    loading: false,
    selectionType: "checkbox",
    multiple: true,
    showConfirm: true,
    showCancel: true,
  });

  // 同步外部加载状态
  useEffect(() => {
    setInternalLoading(loading);
  }, [loading]);

  // 打开对话框
  const openModal = useCallback((initialConfig?: Partial<TableSelectModalConfig>) => {
    setVisible(true);
    if (initialConfig) {
      setConfigState(prev => ({ ...prev, ...initialConfig }));
    }
    setSelectedKeys([]);
    setSelectedRows([]);
  }, []);

  // 关闭对话框
  const closeModal = useCallback(() => {
    setVisible(false);
    setSelectedKeys([]);
    setSelectedRows([]);
  }, []);

  // 更新配置
  const setConfig = useCallback((newConfig: Partial<TableSelectModalConfig>) => {
    setConfigState(prev => ({ ...prev, ...newConfig }));
  }, []);

  // 设置加载状态
  const setLoading = useCallback((loadingState: boolean) => {
    setInternalLoading(loadingState);
    setConfigState(prev => ({ ...prev, loading: loadingState }));
  }, []);

  // 设置选择的键
  const setSelectedKeysCallback = useCallback((keys: React.Key[]) => {
    setSelectedKeys(keys);
  }, []);

  // 设置选择的行
  const setSelectedRowsCallback = useCallback((rows: T[]) => {
    setSelectedRows(rows);
  }, []);

  // 处理选择变化
  const handleSelectionChange = useCallback((keys: React.Key[], rows: T[]) => {
    setSelectedKeys(keys);
    setSelectedRows(rows);

    if (onSelectionChange) {
      onSelectionChange(keys, rows);
    }
  }, [onSelectionChange]);

  // 处理确认
  const handleConfirm = useCallback((onConfirm?: (selectedKeys: React.Key[], selectedRows: T[]) => void) => {
    if (onConfirm && selectedKeys.length > 0) {
      onConfirm(selectedKeys, selectedRows);
    }
    closeModal();
  }, [selectedKeys, selectedRows, closeModal]);

  // 处理取消
  const handleCancel = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // 处理关闭
  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // 更新配置中的加载状态
  const updatedConfig = {
    ...config,
    loading: internalLoading,
  };

  return {
    // 状态
    visible,
    selectedKeys,
    selectedRows,
    loading: internalLoading,

    // 配置
    config: updatedConfig,

    // 方法
    openModal,
    closeModal,
    setConfig,
    setLoading,
    setSelectedKeys: setSelectedKeysCallback,
    setSelectedRows: setSelectedRowsCallback,
    handleSelectionChange,
    handleConfirm,
    handleCancel,
    handleClose,
  };
};

export default useTableSelectModal;
